import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { authHelpers } from '@/lib/auth'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'
import type { AuthUser } from '@/types/auth'

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const AUTH_CHECK_TIMEOUT_MS = 10_000
    let done = false
    const setDone = () => {
      if (!done) {
        done = true
        setLoading(false)
      }
    }

    function toAuthUser(u: { id?: string; email?: string | null; email_confirmed_at?: string | null; created_at?: string; last_sign_in_at?: string | null }): AuthUser | null {
      if (!u?.id || !u?.email) return null
      return {
        id: u.id,
        email: String(u.email),
        emailVerified: !!u.email_confirmed_at,
        createdAt: u.created_at ?? '',
        lastSignInAt: u.last_sign_in_at ?? null,
      }
    }

    // Prefer session from storage so we don't depend on getUser() which can hang after token/setSession
    const timeoutId = setTimeout(setDone, AUTH_CHECK_TIMEOUT_MS)
    authHelpers
      .getSession()
      .then((session) => {
        if (!done && session?.user) {
          const authUser = toAuthUser(session.user)
          if (authUser) {
            setUser(authUser)
            setSentryUser({ id: authUser.id, email: authUser.email })
          }
          setDone()
          return
        }
        if (!done) return authHelpers.getUser()
        return null
      })
      .then((userOrNull) => {
        if (done || userOrNull === undefined) return
        if (userOrNull && typeof userOrNull === 'object' && 'id' in userOrNull) {
          setUser(userOrNull as AuthUser)
          setSentryUser({ id: (userOrNull as AuthUser).id, email: (userOrNull as AuthUser).email })
        } else {
          setUser(userOrNull)
        }
      })
      .catch(() => {
        if (!done) setUser(null)
      })
      .finally(setDone)

    // Listen for auth changes; use session.user to avoid getUser() which can hang
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) {
          const authUser = toAuthUser(session.user)
          setUser(authUser ?? null)
          if (authUser) setSentryUser({ id: authUser.id, email: authUser.email })
        } else {
          setUser(null)
          clearSentryUser()
        }
        setLoading(false)
      }
    )

    return () => {
      clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  }, [])

  const signOut = async () => {
    await authHelpers.signOut()
    setUser(null)
    clearSentryUser()
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
